import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Modal,
  Row,
  Col,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Button,
  Typography,
  Space,
} from "antd";
import { ReloadOutlined, PlusOutlined, CloseCircleFilled } from "@ant-design/icons";
import { fetchComboData, fetchComboDataWithWhere } from "../../services/api";
import { getHeaders } from "../../utility/getHeader";
import { getAuthToken } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Text } = Typography;
const { Option } = Select;

const initialFormState = {
  unit: "",
  quantity_type: "",
  tax: "",
  subcat: "",
  isstockable: false,
  min_stock: "",
  item_code: "",
  barcode: "",
  brand: "",
  supplier_name: "",
  reorder_quantity: "",
  location: "",
  hsn_code: "",
  warranty_months: "",
  shelf_life_days: "",
  batch_tracking: false,
  serial_tracking: false,
};

const NewItemPriceModal = ({ isOpen, onClose, onItemAdded }) => {
  const [formdata, setFormData] = useState(initialFormState);

  const [getTax, setTax] = useState([]);
  const [getUnit, setUnits] = useState([]);
  const [getCategory, setCategory] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [taxType, setTaxType] = useState("");

  const setField = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const selectedImages = Array.from(event.target.files).map((file) =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setImages((prevImages) => [...prevImages, ...selectedImages]);
  };

  const handleDeleteImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const requestBody = {
        iname: formdata.iname,
        unit: formdata.unit,
        weight: formdata.quantity_type,
        tax: formdata.tax,
        mrp: formdata.mrp,
        offerprice: formdata.offerprice,
        catid: formdata.category,
        subcatid: formdata.subcat,
        description: formdata.desc,
        isstockable: formdata.isstockable,
        min_stock: formdata.min_stock,
        item_code: formdata.item_code,
        barcode: formdata.barcode,
        brand: formdata.brand,
        supplier_name: formdata.supplier_name,
        reorder_quantity: formdata.reorder_quantity,
        location: formdata.location,
        hsn_code: formdata.hsn_code,
        warranty_months: formdata.warranty_months,
        shelf_life_days: formdata.shelf_life_days,
        batch_tracking: formdata.batch_tracking,
        serial_tracking: formdata.serial_tracking,
      };

      const post1 = await axios.post(
        "/insertdata/items",
        requestBody,
        getHeaders()
      );

      const formdata1 = e.target;
      const formData = new FormData();

      const hasImages = formdata1.images && formdata1.images.files && formdata1.images.files.length > 0;

      if (hasImages) {
        Array.from(formdata1.images.files).forEach((file) => {
          formData.append("images", file);
        });
      }

      formData.append("product_id", post1.data.id);
      formData.append("use_default", !hasImages ? "true" : "false");

      try {
        const token = getAuthToken();
        if (!token) {
          toast.error('Authentication required. Please log in again.');
          return;
        }

        const authHeaders = getHeaders();

        await axios.post("/addnewproduct/item_images", formData, {
          headers: {
            'Authorization': authHeaders.headers.Authorization
          }
        });
      } catch (imgErr) {
        console.error('Error processing images:', imgErr);

        if (imgErr.response) {
          if (imgErr.response.status === 401) {
            toast.error('Authentication failed. Please log in again.');
          } else if (imgErr.response.status === 403) {
            toast.error('Insufficient permissions to process images.');
          } else if (imgErr.response.status === 500) {
            toast.error('Server error during image processing. Please try again.');
          } else {
            toast.error(`Image processing failed: ${imgErr.response.data?.message || 'Unknown error'}`);
          }
        } else if (imgErr.request) {
          toast.error('Network error during image processing.');
        } else {
          toast.error(`Image processing error: ${imgErr.message}`);
        }
      }

      onItemAdded();
      toast.success("Item added successfully!");
      setImages([]);

      setFormData(initialFormState);

      fetchNextItemCode();

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      toast.error("Error in adding Item");
    }
  };

  const handleCategoryChange = async (value) => {
    setField("category", value);

    if (value) {
      try {
        const whereClause = { cat_id: value };
        const data = await fetchComboDataWithWhere(
          "subcategory",
          "subcat",
          whereClause
        );
        setSubCategories(data);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    } else {
      setSubCategories([]);
    }
  };

  const fetchNextItemCode = async () => {
    try {
      const itemCodeResponse = await axios.get("/items/next-item-code", getHeaders());
      if (itemCodeResponse.data.success) {
        const nextCode = itemCodeResponse.data.data.next_item_code;
        setFormData(prev => ({
          ...prev,
          item_code: nextCode
        }));
        toast.success(`Item code generated: ${nextCode}`);
      }
    } catch (error) {
      console.error("Error fetching next item code:", error);
      toast.error("Failed to generate item code");
    }
  };

  useEffect(() => {
    const fetchComboDataAsync = async () => {
      try {
        const unitsData = await fetchComboData("units", "name");
        setUnits(unitsData);

        const categoriesData = await fetchComboData("categories", "name");
        setCategory(categoriesData);

        if (isOpen && !formdata.item_code) {
          try {
            const itemCodeResponse = await axios.get("/items/next-item-code", getHeaders());
            if (itemCodeResponse.data.success) {
              const nextCode = itemCodeResponse.data.data.next_item_code;
              setFormData(prev => ({
                ...prev,
                item_code: nextCode
              }));
            }
          } catch (error) {
            console.error("Error fetching next item code:", error);
          }
        }

        const coreSettings = await fetchData("coresetting", null, "id", {});

        let currentTaxType = "gst";

        if (coreSettings && coreSettings.length > 0) {
          const taxTypeValue = coreSettings[0].tax_type;
          if (taxTypeValue) {
            currentTaxType = taxTypeValue.toLowerCase();
          }
        }

        setTaxType(currentTaxType);

        let taxOptions = []
        if (currentTaxType === "vat") {
          taxOptions = [
            { id: 1, taxname: "VAT 0%", taxvalue: "0" },
            { id: 2, taxname: "VAT 7%", taxvalue: "7" }
          ];
        } else {
          taxOptions = await fetchComboData("taxes", "taxname");
        }

        setTax(taxOptions);

      } catch (error) {
        console.error("Error fetching combo data:", error);
        try {
          const fallbackTaxOptions = await fetchComboData("taxes", "taxname");
          setTax(fallbackTaxOptions);
        } catch (fallbackError) {
          console.error("Error fetching fallback tax options:", fallbackError);
        }
      }
    };

    if (isOpen) {
      fetchComboDataAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const colProps = { xs: 24, sm: 12, md: 8, lg: 6 };

  return (
    <Modal
      title="Add New Item"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Item Name</Text>
            <Input
              name="iname"
              value={formdata.iname}
              onChange={(e) => setField("iname", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Item Code</Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                name="item_code"
                value={formdata.item_code}
                placeholder="Auto-generated"
                onChange={(e) => setField("item_code", e.target.value)}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchNextItemCode} title="Generate next item code" />
            </Space.Compact>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Barcode</Text>
            <Input
              name="barcode"
              value={formdata.barcode}
              onChange={(e) => setField("barcode", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Unit</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Unit"
              value={formdata.unit || undefined}
              onChange={(value) => setField("unit", value)}
            >
              {getUnit.map((u) => (
                <Option key={u.id} value={u.name}>{u.name}</Option>
              ))}
            </Select>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Item Type</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select type"
              value={formdata.quantity_type || undefined}
              onChange={(value) => setField("quantity_type", value)}
            >
              <Option value="unit">Per Piece</Option>
              <Option value="weight">Per KG</Option>
            </Select>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Tax {taxType ? `(${taxType.toUpperCase()})` : ''}</Text>
            <Select
              style={{ width: '100%' }}
              placeholder={`Select ${taxType ? taxType.toUpperCase() : 'Tax'}`}
              value={formdata.tax || undefined}
              onChange={(value) => setField("tax", value)}
            >
              {getTax.map((t) => (
                <Option key={t.id} value={t.taxvalue}>{t.taxname}</Option>
              ))}
            </Select>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>MRP</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="mrp"
              value={formdata.mrp}
              onChange={(value) => setField("mrp", value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Selling Price</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="offerprice"
              value={formdata.offerprice}
              onChange={(value) => setField("offerprice", value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Category</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Category"
              value={formdata.category || undefined}
              onChange={handleCategoryChange}
            >
              {getCategory.map((c) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>SubCategory</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select SubCategory"
              value={formdata.subcat || undefined}
              onChange={(value) => setField("subcat", value)}
            >
              {subCategories.map((s) => (
                <Option key={s.id} value={s.id}>{s.subcat}</Option>
              ))}
            </Select>
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Description</Text>
            <Input
              name="desc"
              value={formdata.desc}
              onChange={(e) => setField("desc", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Brand</Text>
            <Input
              name="brand"
              value={formdata.brand}
              onChange={(e) => setField("brand", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Supplier</Text>
            <Input
              name="supplier_name"
              value={formdata.supplier_name}
              onChange={(e) => setField("supplier_name", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>HSN/SAC</Text>
            <Input
              name="hsn_code"
              value={formdata.hsn_code}
              onChange={(e) => setField("hsn_code", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Location</Text>
            <Input
              name="location"
              value={formdata.location}
              onChange={(e) => setField("location", e.target.value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Min Stock</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="min_stock"
              value={formdata.min_stock}
              onChange={(value) => setField("min_stock", value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Reorder Qty</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="reorder_quantity"
              value={formdata.reorder_quantity}
              onChange={(value) => setField("reorder_quantity", value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Warranty (Months)</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="warranty_months"
              value={formdata.warranty_months}
              onChange={(value) => setField("warranty_months", value)}
            />
          </Col>

          <Col {...colProps}>
            <Text style={{ fontSize: 13 }}>Shelf Life (Days)</Text>
            <InputNumber
              style={{ width: '100%' }}
              name="shelf_life_days"
              value={formdata.shelf_life_days}
              onChange={(value) => setField("shelf_life_days", value)}
            />
          </Col>

          <Col xs={24} md={12}>
            <Text style={{ fontSize: 13 }}>Inventory Options</Text>
            <div style={{ marginTop: 6 }}>
              <Space wrap>
                <Checkbox
                  checked={formdata.isstockable}
                  onChange={(e) => setField("isstockable", e.target.checked)}
                >
                  Stockable
                </Checkbox>
                <Checkbox
                  checked={formdata.batch_tracking}
                  onChange={(e) => setField("batch_tracking", e.target.checked)}
                >
                  Batch Track
                </Checkbox>
                <Checkbox
                  checked={formdata.serial_tracking}
                  onChange={(e) => setField("serial_tracking", e.target.checked)}
                >
                  Serial Track
                </Checkbox>
              </Space>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <Text style={{ fontSize: 13 }}>Product Images</Text>
            <input
              type="file"
              name="images"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'block', marginTop: 6 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Optional - default used if none selected
            </Text>
          </Col>

          {images.length > 0 && (
            <Col xs={24}>
              <Space wrap>
                {images.map((image, index) => (
                  <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={image.preview}
                      alt="preview"
                      style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
                    />
                    <CloseCircleFilled
                      onClick={() => handleDeleteImage(index)}
                      style={{ position: 'absolute', top: -6, right: -6, color: '#ef4444', background: '#fff', borderRadius: '50%', fontSize: 16, cursor: 'pointer' }}
                    />
                  </div>
                ))}
              </Space>
            </Col>
          )}

          <Col xs={24} style={{ textAlign: 'right', marginTop: 8 }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                Add Item
              </Button>
            </Space>
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default NewItemPriceModal;
